import type React from "react"
import { Col, Row } from "reactstrap"
import type { RealTimeStats as RealTimeStatsType } from "./types/dashboard"
import { formatCurrency } from "./dashboardHelpers"
import "../../CommonElements/DashboardTheme/dashboard-theme.css"

interface RealTimeStatsProps {
  realTimeStats: RealTimeStatsType | null
  loading: boolean
}

const RealTimeStats: React.FC<RealTimeStatsProps> = ({ realTimeStats, loading }) => {
  // Show placeholder data if realTimeStats is null or loading
  const displayStats = realTimeStats || {
    activeVisitors: 0,
    todayEntries: 0,
    todayExits: 0,
    todayVehicleRevenue: 0,
    todayVerifications: 0,
    todayTotalBadgePrints: 0,
    todayFreeOriginalBadges: 0,
    todayPaidCopyBadges: 0,
    todayPaidCopyBadgeRevenue: 0,
    todayTicketSalesCount: 0,
    todayTicketRevenue: 0,
    todayUsedTickets: 0,
    todayUnusedTickets: 0,
    todayPaidCheckouts: 0,
    todayWhitelistedCheckouts: 0,
  }

  return (
    <div className="db-live-banner db-bg-brand">
      <div className="db-live-banner-head">
        <div className="db-live-banner-title">
          <i className="fa fa-bolt"></i>
          Today's Live Activity
        </div>
        <div className="db-live-pill">
          <span className="db-live-dot"></span>
          Live
        </div>
      </div>

      {loading ? (
        <Row className="g-2">
          {Array.from({ length: 8 }).map((_, index) => (
            <Col lg="3" md="4" sm="6" xs="6" key={index} className="mb-2">
              <div className="db-live-metric">
                <div className="db-skeleton mb-1" style={{ height: "22px", width: "100%", background: "rgba(255,255,255,0.2)" }}></div>
                <div className="db-skeleton" style={{ height: "10px", width: "70%", margin: "0 auto", background: "rgba(255,255,255,0.2)" }}></div>
              </div>
            </Col>
          ))}
        </Row>
      ) : (
        <Row className="g-3">
          {/* First Column: Vehicle Data */}
          <Col lg="6" md="6" sm="12">
            <div className="db-live-group">
              <div className="db-live-group-title">
                <i className="fa fa-car"></i>
                Vehicle Activity
              </div>
              <Row className="g-2">
                <Col lg="6" md="12" sm="6" xs="6">
                  <div className="db-live-metric">
                    <div className="db-live-value">{displayStats.todayEntries}</div>
                    <div className="db-live-label">
                      <i className="fa fa-sign-in"></i>
                      Check-ins
                    </div>
                  </div>
                </Col>
                <Col lg="6" md="12" sm="6" xs="6">
                  <div className="db-live-metric">
                    <div className="db-live-value">{displayStats.todayExits}</div>
                    <div className="db-live-label">
                      <i className="fa fa-sign-out"></i>
                      Checked Out
                    </div>
                  </div>
                </Col>
                <Col lg="6" md="12" sm="6" xs="6">
                  <div className="db-live-metric">
                    <div className="db-live-value">{displayStats.activeVisitors}</div>
                    <div className="db-live-label">
                      <i className="fa fa-car"></i>
                      Still in Parking
                    </div>
                  </div>
                </Col>
                <Col lg="6" md="12" sm="6" xs="6">
                  <div className="db-live-metric">
                    <div className="db-live-value">{formatCurrency(displayStats.todayVehicleRevenue)}</div>
                    <div className="db-live-label">
                      <i className="fa fa-money"></i>
                      Vehicle Revenue
                    </div>
                  </div>
                </Col>
                <Col lg="6" md="12" sm="6" xs="6">
                  <div className="db-live-metric">
                    <div className="db-live-value">{displayStats.todayPaidCheckouts || 0}</div>
                    <div className="db-live-label">
                      <i className="fa fa-credit-card"></i>
                      Paid
                    </div>
                  </div>
                </Col>
                <Col lg="6" md="12" sm="6" xs="6">
                  <div className="db-live-metric">
                    <div className="db-live-value">{displayStats.todayWhitelistedCheckouts || 0}</div>
                    <div className="db-live-label">
                      <i className="fa fa-list-alt"></i>
                      Whitelisted
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>

          {/* Second Column: Ticket Data */}
          <Col lg="6" md="6" sm="12">
            <div className="db-live-group">
              <div className="db-live-group-title">
                <i className="fa fa-ticket"></i>
                Ticket Activity
              </div>
              <Row className="g-2">
                <Col lg="6" md="12" sm="6" xs="6">
                  <div className="db-live-metric">
                    <div className="db-live-value">{displayStats.todayTicketSalesCount}</div>
                    <div className="db-live-label">
                      <i className="fa fa-shopping-cart"></i>
                      Tickets Sold
                    </div>
                  </div>
                </Col>
                <Col lg="6" md="12" sm="6" xs="6">
                  <div className="db-live-metric">
                    <div className="db-live-value">{formatCurrency(displayStats.todayTicketRevenue)}</div>
                    <div className="db-live-label">
                      <i className="fa fa-dollar"></i>
                      Ticket Revenue
                    </div>
                  </div>
                </Col>
                <Col lg="6" md="12" sm="6" xs="6">
                  <div className="db-live-metric">
                    <div className="db-live-value">{displayStats.todayUsedTickets}</div>
                    <div className="db-live-label">
                      <i className="fa fa-check-square-o"></i>
                      Used Tickets
                    </div>
                  </div>
                </Col>
                <Col lg="6" md="12" sm="6" xs="6">
                  <div className="db-live-metric">
                    <div className="db-live-value">{displayStats.todayUnusedTickets}</div>
                    <div className="db-live-label">
                      <i className="fa fa-square-o"></i>
                      Unused Tickets
                    </div>
                  </div>
                </Col>
                <Col lg="6" md="12" sm="6" xs="6">
                  <div className="db-live-metric">
                    <div className="db-live-value">{displayStats.todayVerifications}</div>
                    <div className="db-live-label">
                      <i className="fa fa-check-circle-o"></i>
                      Verifications
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      )}
    </div>
  )
}

export default RealTimeStats
